import { CdkDropList } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  CurrentPatientScope,
  GlobalStoreService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  ChartedTreatment,
  getServiceCodeItems,
} from '@principle-theorem/principle-core';
import {
  IChartedMultiStepTreatment,
  IAppointment,
  IChartedMultiStepTreatmentStep,
  IChartedRef,
  IChartedTreatment,
  IChartedTreatmentGroup,
  IFeeSchedule,
  IServiceCode,
  IServiceCodeEntry,
  ITreatmentCategory,
  ITreatmentConfiguration,
  ITreatmentPlan,
  ITreatmentStep,
  ServiceCodeGroupType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asyncForEach,
  DocumentReference,
  filterUndefined,
  Firestore,
  isSameRef,
  multiMap,
  reduce2DArray,
  shareReplayCold,
  WithRef,
} from '@principle-theorem/shared';
import { sortBy, uniqBy } from 'lodash';
import { Observable, of } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import { IEditChartableData } from '../../chartable-surface-updater';
import { ChartedItemActionsService } from '../../services/charted-item-actions.service';
import { ServiceCodeManagerService } from '../../services/service-code-manager.service';
import {
  ITreatmentDragDropNode,
  TreatmentItem,
} from '../../treatment-drag-drop-group';

@Component({
  selector: 'pr-treatment-scope-grouped',
  templateUrl: './treatment-scope-grouped.component.html',
  styleUrl: './treatment-scope-grouped.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [ChartedItemActionsService],
})
export class TreatmentScopeGroupedComponent {
  private _organisation = inject(OrganisationService);
  private _patientScope = inject(CurrentPatientScope);
  private _serviceCodeManager = inject(ServiceCodeManagerService);
  private _globalStore = inject(GlobalStoreService);
  private _chartedItemActions = inject(ChartedItemActionsService);
  public elementRef = inject(ElementRef);

  treatment = input.required<IChartedTreatmentGroup>();
  selectedSurfaces = input<Partial<IChartedRef>[]>();
  expanded = input<boolean>(false);
  disabled = input<boolean>(false);
  compact = input<boolean>(false);
  plan = input<WithRef<ITreatmentPlan> | IChartedMultiStepTreatment>();
  step = input<WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep>();
  connectedLists = input<CdkDropList<TreatmentItem>[]>([]);
  appointment = input<WithRef<IAppointment>>();
  patient = toSignal(this._patientScope.doc$.pipe(filterUndefined()));

  treatmentChanged = output<IChartedTreatment | IChartedTreatment[]>();
  treatmentDeleted = output<IChartedTreatment | IChartedTreatment[]>();
  categoryChanged = output<WithRef<ITreatmentCategory>>();
  updateChartable = output<IEditChartableData>();
  expandedChanged = output<boolean>();
  treatmentGroupChanged = output<IChartedTreatmentGroup>();

  groupExpanded = signal(false);
  expandAll = signal(false);
  expandedState = computed(() => this.expanded() || this.groupExpanded());
  connectedListIds = signal<string[]>([]);
  nestedListData = computed<ITreatmentDragDropNode>(() => ({
    parent: {
      plan: this.plan(),
      step: this.step(),
    },
    items: this.treatment().treatments,
  }));

  config$: Observable<WithRef<ITreatmentConfiguration>>;
  commonCodes$: Observable<IServiceCodeEntry[]>;
  serviceCodeSummary$: Observable<string>;
  feeSchedule$: Observable<WithRef<IFeeSchedule> | undefined>;
  treatmentCategory$: Observable<
    DocumentReference<ITreatmentCategory> | undefined
  >;

  constructor() {
    this.config$ = toObservable(this.treatment).pipe(
      map((treatment) => treatment.config),
      switchMap((config) => Firestore.getDoc(config.ref)),
      shareReplayCold()
    );

    this.treatmentCategory$ = this.config$.pipe(
      map(({ category }) => category)
    );

    this.commonCodes$ = this.config$.pipe(
      map((config) =>
        config.serviceCodeGroups
          .filter((group) => group.type === ServiceCodeGroupType.Optional)
          .map((group) => group.serviceCodes)
      ),
      reduce2DArray()
    );

    this.serviceCodeSummary$ = toObservable(this.treatment).pipe(
      withLatestFrom(this._organisation.taxRate$.pipe(filterUndefined())),
      map(([treatment, taxRate]) => {
        const allServiceCodeItems = treatment.treatments.flatMap(
          (chartedTreatment) => getServiceCodeItems(chartedTreatment, taxRate)
        );

        return uniqBy(allServiceCodeItems, (code) => code.code);
      }),
      multiMap((serviceCode) => serviceCode.code),
      map((serviceCodes) => sortBy(serviceCodes).join(', '))
    );

    this.feeSchedule$ = toObservable(this.treatment).pipe(
      switchMap((groupedTreatment) => {
        const treatments = groupedTreatment.treatments;
        const firstFeeSchedule = treatments[0]?.feeSchedule;
        if (!firstFeeSchedule) {
          return of(undefined);
        }

        const allSame = treatments.every((treatment) =>
          isSameRef(treatment.feeSchedule, firstFeeSchedule)
        );

        return allSame
          ? this._globalStore.getFeeSchedule$(firstFeeSchedule.ref)
          : of(undefined);
      })
    );

    toObservable(this.connectedLists)
      .pipe(
        map((lists) => lists.map((list) => list.id)),
        takeUntilDestroyed()
      )
      .subscribe((ids) => this.connectedListIds.set(ids));
  }

  setGroupExpanded(expanded: boolean): void {
    this.groupExpanded.set(expanded);
    this.expandedChanged.emit(expanded);
    if (!expanded) {
      this.expandAll.set(false);
    }
  }

  toggleExpandAll(): void {
    this.expandAll.update((currentVal) => !currentVal);
  }

  async deleteTreatment(treatment: IChartedTreatmentGroup): Promise<void> {
    const confirmed = await this._chartedItemActions.confirmDelete(treatment);
    if (!confirmed) {
      return;
    }

    this.treatmentDeleted.emit(treatment.treatments);
  }

  async editSurfaces(): Promise<void> {
    const item = this.treatment();
    const chartable = await Firestore.getDoc(item.config.ref);

    const editData = await this._chartedItemActions.editSurfaces(
      chartable,
      item
    );

    if (!editData) {
      return;
    }

    this.updateChartable.emit(editData);
  }

  async addBasePrice(treatmentGroup: IChartedTreatmentGroup): Promise<void> {
    const currentBasePrice = ChartedTreatment.getBasePrice(
      treatmentGroup.treatments[0]
    );
    const newBasePrice =
      await this._chartedItemActions.addBasePrice(currentBasePrice);

    if (newBasePrice === undefined) {
      return;
    }

    const updatedTreatments = treatmentGroup.treatments.map((treatment) => ({
      ...treatment,
      basePriceOverride: newBasePrice,
    }));

    this.treatmentChanged.emit(updatedTreatments);
  }

  async setFeeSchedule(feeSchedule: WithRef<IFeeSchedule>): Promise<void> {
    const treatment = this.treatment();
    const step = this.step();

    const updatedTreatments = await this._chartedItemActions.applyFeeSchedule(
      treatment.treatments,
      feeSchedule,
      step?.treatments ?? []
    );

    this.treatmentChanged.emit(updatedTreatments);
  }

  async updateServiceCodes(
    treatments: IChartedTreatment[],
    serviceCode: IServiceCode
  ): Promise<void> {
    const step = this.step();
    const stepTreatments = step?.treatments ?? [];
    const stepServiceCodes = stepTreatments.flatMap(
      (stepTreatment) => stepTreatment.serviceCodes
    );

    const updatedTreatments = await asyncForEach(
      treatments,
      async (treatment) => {
        const updatedServiceCodes =
          await this._serviceCodeManager.addServiceCodeWithFeeSchedule(
            treatment.serviceCodes,
            stepServiceCodes,
            serviceCode,
            treatment,
            this._globalStore.getFeeSchedule$(treatment.feeSchedule.ref)
          );

        return {
          ...treatment,
          serviceCodes: updatedServiceCodes,
        };
      }
    );

    this.treatmentChanged.emit(updatedTreatments);
  }
}
